import { call, put, takeEvery } from 'redux-saga/effects';
import { SendApiRequest } from 'src/api';
import { apiRoutes } from 'src/api/routes';
import {
    fetchShifts,
    shiftsFetched,
    shiftsFetchRequestFailed,
    createShift,
    updateShift,
    shiftCreated,
    shiftUpdated,
    shiftUpdateFailed,
    shiftCreationFailed,
    fetchPreviousShifts,
    previousShiftsFetched,
    previousShiftsFetchRequestFailed,
    createTrackerRequest,
    updateTrackerRequest,
    createTrackerSuccess,
    updateTrackerSuccess,
    createTrackerFailed,
    updateTrackerFailed,
    contractorTrackedTimeUpdated,
    finalizeBilling,
    billingFinalized,
    billingFinalizeFailed,
    endEditing,
} from '../shifts';
import { showToastAction } from '../ui';
import moment from 'moment';
import {
    calculateTotalTimeWorkedWithPause,
    formatTimeFromMilliseconds,
} from 'src/utils/shift';

function* fetchShiftsEffect({ payload }) {
    try {
        const response = yield call(SendApiRequest, {
            url: apiRoutes.jobs,
            method: 'GET',
            queryParams: { ...payload, preset: 'upcoming_ongoing' },
        });
        yield put(
            shiftsFetched({
                results: response.results,
                totalRecords: response.totalRecords,
            }),
        );
    } catch (e) {
        yield put(shiftsFetchRequestFailed());
    }
}

function* fetchPreviousShiftsEffect({ payload }) {
    try {
        const response = yield call(SendApiRequest, {
            // Use unified jobs endpoint with preset for past shifts
            url: apiRoutes.jobs,
            method: 'GET',
            queryParams: { ...payload, preset: 'past_shifts' },
        });

        yield put(
            previousShiftsFetched({
                results: response.jobs || response.results,
                totalRecords: response.totalRecords,
            }),
        );
    } catch (e) {
        yield put(previousShiftsFetchRequestFailed());
    }
}

function* createShiftEffect(action) {
    const {
        payload: { resolve, reject, data },
    } = action;
    try {
        let apiUrl = apiRoutes.jobs;
        if (data.shiftType === 'multi') {
            apiUrl = apiRoutes.createMultiDayJobs;
        } else if (data.shiftType === 'recurring') {
            apiUrl = apiRoutes.createRecurringJobs;
        }
        const response = yield call(SendApiRequest, {
            url: apiUrl,
            method: 'POST',
            body: data,
        });
        yield put(
            shiftCreated({
                data: response.data,
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(shiftCreationFailed());
        yield call(reject, e);
    }
}

function* updateShiftEffect(action) {
    const {
        payload: { resolve, reject, data },
    } = action;
    try {
        let apiUrl = apiRoutes.updateJob(data.shiftId);
        if (data.shiftType === 'multi') {
            apiUrl = apiRoutes.updateMultiDayJob(data.groupShiftId);
        } else if (data.shiftType === 'recurring') {
            apiUrl = apiRoutes.updateRecurringJob(data.groupShiftId);
        }
        const response = yield call(SendApiRequest, {
            url: apiUrl,
            method: 'PUT',
            body: data,
        });
        yield put(
            shiftUpdated({
                data: response.data,
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(shiftUpdateFailed());
        yield call(reject, e);
    }
}

function* createTrackerEffect(action) {
    try {
        const {
            job,
            stafferAndShift,
            totalHours,
            chargeManualHoursFee = true,
            manualHoursFeeWaiverReason = '',
            manualHourFeeRemovalApproval = false,
        } = action.payload;

        // stafferAndShift.staffer is always an object (populated from backend)
        const stafferId = stafferAndShift.staffer._id;

        // Prepare request body (no invoiceId, backend will extract from job)
        const requestBody = {
            chargeManualHoursFee: chargeManualHoursFee,
            manualHoursFeeWaiverReason: manualHoursFeeWaiverReason,
            manualHourFeeRemovalApproval: manualHourFeeRemovalApproval,
            jobId: job._id,
            stafferId,
            startEditingInvoice: job.startEditingInvoice || false,
            startTime: job.shiftStartTime,
            stopTime: calculateShiftEndTime(totalHours, job),
        };

        const response = yield call(SendApiRequest, {
            url: apiRoutes.createTracker,
            method: 'POST',
            body: requestBody,
        });

        const updatedTracker =
            (response && response.data && response.data.tracker) || null;

        const updatedInvoice =
            (response && response.data && response.data.invoice) || null;

        const durationMs = calculateTotalTimeWorkedWithPause(updatedTracker);
        const pendingTimeDisplay = formatTimeFromMilliseconds(durationMs);
        yield put(
            contractorTrackedTimeUpdated({
                shiftId: job._id,
                contractorId: stafferId,
                trackedTime: pendingTimeDisplay,
                tracker: updatedTracker || undefined,
                invoice: updatedInvoice || undefined,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Invoice updated successfully',
            }),
        );

        yield put(createTrackerSuccess());
    } catch (error) {
        console.log(JSON.stringify(error));
        yield put(createTrackerFailed(error.message));
    }
}

function* updateTrackerEffect(action) {
    try {
        const {
            job,
            stafferAndShift,
            totalHours,
            tracker,
            chargeManualHoursFee = true,
            manualHoursFeeWaiverReason = '',
            manualHourFeeRemovalApproval = false,
        } = action.payload;

        if (!tracker || !tracker._id) {
            yield put(
                showToastAction({
                    type: 'danger',
                    text: 'Tracker ID is required for update operation.',
                }),
            );
            yield put(updateTrackerFailed('Missing trackerId'));
            return;
        }

        // Get stafferId for Redux action (backend will derive from tracker.user)
        const stafferId = stafferAndShift.staffer._id;

        // Prepare request body (no invoiceId, jobId, or stafferId - backend will extract from tracker)
        const requestBody = {
            chargeManualHoursFee: chargeManualHoursFee,
            manualHoursFeeWaiverReason: manualHoursFeeWaiverReason,
            manualHourFeeRemovalApproval: manualHourFeeRemovalApproval,
            startEditingInvoice: job.startEditingInvoice || false,
            startTime: job.shiftStartTime,
            stopTime: calculateShiftEndTime(totalHours, job),
        };

        const response = yield call(SendApiRequest, {
            url: apiRoutes.updateTracker(tracker._id),
            method: 'PATCH',
            body: requestBody,
        });

        const updatedTracker =
            (response && response.data && response.data.tracker) ||
            tracker ||
            null;

        const updatedInvoice =
            (response && response.data && response.data.invoice) || null;

        const durationMs = calculateTotalTimeWorkedWithPause(updatedTracker);
        const pendingTimeDisplay = formatTimeFromMilliseconds(durationMs);
        yield put(
            contractorTrackedTimeUpdated({
                shiftId: job._id,
                contractorId: stafferId,
                trackedTime: pendingTimeDisplay,
                tracker: updatedTracker || tracker || undefined,
                invoice: updatedInvoice || undefined,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Invoice updated successfully',
            }),
        );

        yield put(updateTrackerSuccess());
    } catch (error) {
        console.log(JSON.stringify(error));
        yield put(updateTrackerFailed(error.message));
    }
}

function* finalizeBillingEffect(action) {
    try {
        // The reducer already sets loading state when action is dispatched
        if (!action.payload) {
            yield put(billingFinalizeFailed());
            yield put(
                showToastAction({
                    type: 'danger',
                    text: 'Missing payload in finalizeBilling action',
                }),
            );
            return;
        }

        const { invoiceId, jobId } = action.payload;

        if (!invoiceId || !jobId) {
            yield put(billingFinalizeFailed());
            yield put(
                showToastAction({
                    type: 'danger',
                    text: 'Missing required parameters (invoiceId or jobId)',
                }),
            );
            return;
        }

        // Call the billing finalize API using new invoice-payment endpoint
        yield call(SendApiRequest, {
            url: apiRoutes.finalizeBilling(invoiceId),
            method: 'POST',
            body: {
                jobId: jobId,
            },
        });

        yield put(billingFinalized());
        yield put(endEditing({ shiftId: jobId }));
        yield put(
            showToastAction({
                type: 'success',
                text: 'Billing finalized successfully',
            }),
        );

        // Reload the page to reflect all changes
        // window.location.reload();
    } catch (error) {
        yield put(billingFinalizeFailed());
        // Error toast is already shown by axios response interceptor
        // No need to show duplicate toast here
    }
}

function getDurationMilliseconds(totalHours) {
    if (totalHours === null || totalHours === undefined) {
        return 0;
    }

    if (typeof totalHours === 'number') {
        return totalHours;
    }

    if (typeof totalHours === 'string') {
        const parts = totalHours.split(':').map(value => parseInt(value, 10));
        if (parts.every(part => !Number.isNaN(part))) {
            const [hours = 0, minutes = 0, seconds = 0] = parts;
            return ((hours * 60 + minutes) * 60 + seconds) * 1000;
        }
    }

    return moment.duration(totalHours).asMilliseconds();
}

function calculateShiftEndTime(totalHours, job) {
    // Calculate shift end time based on tracker data
    // This should match the logic from the original Admin 1.0 code
    const durationMs = getDurationMilliseconds(totalHours);
    let editHours = durationMs / (1000 * 60 * 60);

    let shiftEndTime = moment
        .unix(job.shiftStartTime)
        .tz(job.timeZone)
        .add(editHours, 'hours')
        .tz('America/Toronto')
        .format('X');
    return shiftEndTime;
}

// function findStafferTracker(job, stafferId) {
//     // Find the tracker for a specific staffer in the job
//     const stafferAndShift = job.staffersAndShifts.find(
//         s => s.staffer._id === stafferId,
//     );
//     return stafferAndShift ? stafferAndShift.tracker : null;
// }

export const shiftsSagas = [
    takeEvery(fetchShifts.type, fetchShiftsEffect),
    takeEvery(fetchPreviousShifts.type, fetchPreviousShiftsEffect),
    takeEvery(createShift.type, createShiftEffect),
    takeEvery(updateShift.type, updateShiftEffect),
    takeEvery(createTrackerRequest.type, createTrackerEffect),
    takeEvery(updateTrackerRequest.type, updateTrackerEffect),
    takeEvery(finalizeBilling.type, finalizeBillingEffect),
];
